<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import axios from 'axios'
import flatPickr from 'vue-flatpickr-component'
import { Russian } from 'flatpickr/dist/l10n/ru.js'
import moment from 'moment'

const API = 'https://api.client.za-bota.com'

export default {
    page: {
        title: 'Экономика',
        meta: [{ name: 'description', content: appConfig.description }],
    },
    components: { Layout, flatPickr },
    data() {
        return {
            title: 'Экономика (доходы / расходы / маржа)',
            loading: false,
            selectedDate: [moment().subtract(6, 'days').toDate(), new Date()],
            date_start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
            date_end: moment().format('YYYY-MM-DD'),
            dateConfig: { mode: 'range', locale: Russian, dateFormat: 'Y-m-d' },
            fl_ai: '',
            group_by: 'day',
            bot_id: '',
            bot_ids: [],
            botOptions: [],
            botSearch: '',
            user_id: '',
            user_label: '',
            totals: null,
            rows: [],
            labels: {},
            users: {},
            sortBy: null,
            sortDesc: true,
            providerKeys: [],
            providerLabels: {},
            callProviderKeys: [],
            callProviderLabels: {},
            cashflow: [],
            calls: { rows: [], total: 0, page: 1, per_page: 50 },
            showCalls: false,
            callDetail: null,
            groupFields: {
                day:  [{ key: 'grp', label: 'День' }],
                bot:  [{ key: 'grp', label: 'Бот' }],
                user: [{ key: 'grp', label: 'Клиент' }],
            },
        }
    },
    computed: {
        token() {
            return window.localStorage.getItem('API_KEY_ZABOTA')
        },
        botDropdownText() {
            if (!this.bot_ids.length) return 'Все боты'
            if (this.bot_ids.length === 1) {
                const b = this.botOptions.find((x) => x.id === this.bot_ids[0])
                return b ? b.name : 'Выбран 1 бот'
            }
            return 'Выбрано ботов: ' + this.bot_ids.length
        },
        filteredBotOptions() {
            const s = this.botSearch.trim().toLowerCase()
            if (!s) return this.botOptions
            return this.botOptions.filter((b) => b.name.toLowerCase().includes(s))
        },
        // Итоги по каждому столбцу таблицы (сумма видимых строк) — строка над таблицей
        colTotals() {
            const sumKeys = ['calls_cnt', 'attempts_cnt', 'minutes', 'minutes_out', 'revenue', 'buy_attempts',
                'buy_telephony', 'buy_other', 'buy_cost', 'margin',
                ...this.providerKeys.map((k) => 'prov_' + k)]
            const t = {}
            sumKeys.forEach((k) => { t[k] = 0 })
            this.rows.forEach((r) => {
                sumKeys.forEach((k) => { t[k] += Number(r[k]) || 0 })
            })
            sumKeys.forEach((k) => { t[k] = Math.round(t[k] * 100) / 100 })
            t.margin_pct = t.revenue > 0 ? Math.round(((t.revenue - t.buy_cost) / t.revenue) * 1000) / 10 : null
            return t
        },
        tableFields() {
            if (!this.groupFields[this.group_by]) return []
            const grp = this.groupFields[this.group_by][0]
            // все столбцы сортируются кликом по заголовку; первый — по подписи (имя бота/клиента), а не по id
            return [
                { key: grp.key, label: grp.label, sortable: true, sortByFormatted: true, formatter: (v, k, item) => this.rowLabel(item) },
                { key: 'revenue', label: 'Доход ₽', sortable: true, formatter: this.numFmt, tdClass: 'font-weight-bold', thClass: 'font-weight-bold' },
                { key: 'calls_cnt', label: 'Звонков', sortable: true, formatter: this.numFmt },
                { key: 'attempts_cnt', label: 'Попыток', sortable: true, formatter: this.numFmt },
                { key: 'minutes', label: 'Минут', sortable: true, formatter: this.numFmt },
                { key: 'minutes_out', label: 'Мин. исх.', sortable: true, formatter: this.numFmt },
                { key: 'buy_attempts', label: 'Этикетка ₽', sortable: true, formatter: this.costFmt },
                { key: 'buy_telephony', label: 'Телеф. ₽', sortable: true, formatter: this.costFmt },
                ...this.providerKeys.map((k) => ({ key: 'prov_' + k, label: (this.providerLabels[k] || k) + ' ₽', sortable: true, formatter: this.costFmt })),
                { key: 'buy_other', label: 'Прочее ₽', sortable: true, formatter: this.costFmt },
                { key: 'buy_cost', label: 'Себест. ₽', sortable: true, formatter: this.costFmt },
                { key: 'margin', label: 'Маржа ₽', sortable: true, formatter: this.numFmt },
                { key: 'margin_pct', label: 'Маржа %', sortable: true },
            ]
        },
    },
    async mounted() {
        await this.getData()
        await this.loadBotOptions()
    },
    methods: {
        fmt(v) {
            if (v === null || v === undefined || v === '') return '—'
            return Number(v).toLocaleString('ru-RU')
        },
        // «(x%)» — доля статьи расходов от дохода; пусто, если дохода нет
        pctStr(v, revenue) {
            const r = Number(revenue)
            const x = Number(v)
            if (!r || r <= 0 || isNaN(x)) return ''
            return ' (' + Math.round((x / r) * 1000) / 10 + '%)'
        },
        numFmt(v) {
            if (v === null || v === undefined || v === '') return '—'
            return Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
        },
        costFmt(v, key, item) {
            const base = v === null || v === undefined || v === '' ? '—' : this.numFmt(v)
            return base + this.pctStr(v, item.revenue)
        },
        userInfo(id) {
            return this.users[id] || null
        },
        userTitle(id) {
            const u = this.userInfo(id)
            if (!u) return this.labels[id] || 'Клиент #' + id
            if (u.name && u.phone) return u.name + ' · ' + u.phone
            return u.name || u.phone || u.email || 'Клиент #' + id
        },
        userBalanceStr(id) {
            const u = this.userInfo(id)
            if (!u || u.balance === null || u.balance === undefined) return ''
            return ' (баланс ' + Number(u.balance).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽)'
        },
        userLink(id) {
            const u = this.userInfo(id)
            return u && u.uuid ? '/account/users/' + u.uuid + '/edit' : null
        },
        isCostKey(k) {
            return k === 'buy_attempts' || k === 'buy_telephony' || k === 'buy_other' || k === 'buy_cost' || k.indexOf('prov_') === 0
        },
        baseParams() {
            const p = { token: this.token, from: this.date_start, to: this.date_end }
            if (this.fl_ai !== '') p.fl_ai = this.fl_ai
            if (this.bot_id) p.bot_id = this.bot_id
            if (this.bot_ids.length) p.bot_ids = this.bot_ids.join(',')
            if (this.user_id) p.user_id = this.user_id
            return p
        },
        // Справочник ботов для мультивыбора: только боты с активностью за период
        // и с учётом фильтра «Тип ботов» (AI / не-AI)
        async loadBotOptions() {
            try {
                const params = { token: this.token, from: this.date_start, to: this.date_end, group_by: 'bot' }
                if (this.fl_ai !== '') params.fl_ai = this.fl_ai
                const resp = await axios.get(API + '/v1/economy/summary', { params })
                const labels = resp.data.data.labels || {}
                const rows = resp.data.data.rows || []
                this.botOptions = rows
                    .filter((r) => Number(r.calls_cnt) > 0 || Number(r.revenue) > 0 || Number(r.buy_cost) > 0)
                    .map((r) => ({ id: r.grp, name: (labels[r.grp] || 'Бот #' + r.grp) + (r.fl_ai ? ' 🤖' : '') }))
                    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                // выбранные боты, выпавшие из списка (сменился период/тип) — убираем из фильтра
                const alive = new Set(this.botOptions.map((b) => b.id))
                const kept = this.bot_ids.filter((id) => alive.has(id))
                if (kept.length !== this.bot_ids.length) {
                    this.bot_ids = kept
                    await this.getData()
                }
            } catch (e) {
                this.botOptions = []
            }
        },
        async getData() {
            this.loading = true
            try {
                if (this.group_by === 'calls') {
                    const sumNone = await axios.get(API + '/v1/economy/summary', { params: { ...this.baseParams(), group_by: 'none' } })
                    this.totals = (sumNone.data.data.rows || [])[0] || null
                    this.rows = []
                    await this.openCalls()
                    this.loading = false
                    return
                }
                const [sumNone, sumGrp, cash] = await Promise.all([
                    axios.get(API + '/v1/economy/summary', { params: { ...this.baseParams(), group_by: 'none' } }),
                    axios.get(API + '/v1/economy/summary', { params: { ...this.baseParams(), group_by: this.group_by } }),
                    axios.get(API + '/v1/economy/cashflow', { params: this.baseParams() }),
                ])
                this.totals = (sumNone.data.data.rows || [])[0] || null
                this.rows = sumGrp.data.data.rows || []
                this.labels = sumGrp.data.data.labels || {}
                this.users = sumGrp.data.data.users || {}
                this.providerKeys = sumGrp.data.data.provider_keys || []
                this.providerLabels = sumGrp.data.data.provider_labels || {}
                this.rows.forEach((r) => {
                    Object.keys(r).forEach((k) => {
                        if (k !== 'grp' && typeof r[k] === 'string' && r[k].trim() !== '' && !isNaN(r[k])) r[k] = Number(r[k])
                    })
                    const p = r.providers || {}
                    this.providerKeys.forEach((k) => { r['prov_' + k] = p[k] !== undefined ? p[k] : 0 })
                })
                this.cashflow = cash.data.data.rows || []
                if (this.showCalls) await this.getCalls()
            } finally {
                this.loading = false
            }
        },
        async getCalls() {
            const resp = await axios.get(API + '/v1/economy/calls', {
                params: { ...this.baseParams(), page: this.calls.page, per_page: this.calls.per_page },
            })
            this.calls.rows = resp.data.data.rows || []
            this.calls.total = resp.data.data.total || 0
            this.callProviderKeys = resp.data.data.provider_keys || []
            this.callProviderLabels = resp.data.data.provider_labels || {}
        },
        async openCalls() {
            this.showCalls = true
            this.calls.page = 1
            await this.getCalls()
        },
        async openCallDetail(uuid) {
            const resp = await axios.get(API + '/v1/economy/call/' + uuid, { params: { token: this.token } })
            this.callDetail = resp.data.data
            this.$bvModal.show('call-detail-modal')
        },
        rowLabel(row) {
            if (this.group_by === 'bot') return (this.labels[row.grp] || 'Бот #' + row.grp) + (row.fl_ai ? ' 🤖' : '')
            if (this.group_by === 'user') return this.userTitle(row.grp) + this.userBalanceStr(row.grp)
            return row.grp
        },
        async rowClicked(row) {
            if (this.group_by === 'bot') {
                this.bot_id = row.grp
                await this.openCalls()
                await this.getData()
            }
            if (this.group_by === 'user') {
                this.user_id = row.grp
                this.user_label = this.userTitle(row.grp)
                await this.getData()
            }
        },
        async resetFilters() {
            this.bot_id = ''
            this.bot_ids = []
            this.user_id = ''
            this.user_label = ''
            this.fl_ai = ''
            this.showCalls = false
            await this.getData()
        },
        async onChangePeriod(item) {
            if (item[0] && item[1]) {
                this.date_start = moment(item[0]).format('YYYY-MM-DD')
                this.date_end = moment(item[1]).format('YYYY-MM-DD')
                await this.getData()
                await this.loadBotOptions()
            }
        },
        exportUrl(level) {
            const p = new URLSearchParams({ ...this.baseParams(), level })
            return API + '/v1/economy/export?' + p.toString()
        },
        billingLabel(item) {
            const map = { stt: 'Распознавание', tts: 'Синтез', llm: 'LLM', sms_send: 'SMS', voicemail_detection: 'Автоответчик', digital_input: 'Цифровой ввод' }
            return map[item.product] || item.product
        },
        billingQty(item) {
            if (item.product === 'stt') return (item.duration || 0) + ' сек'
            if (item.product === 'tts') return (item.length || 0) + ' симв' + (item.from_cache ? ' (кэш)' : '')
            if (item.product === 'llm') {
                if (item.from_cache) return 'ответ из кэша'
                return (item.prompt_tokens || 0) + '+' + (item.completion_tokens || 0) + ' ток (' + (item.cached_tokens || 0) + ' кэш)'
            }
            return ''
        },
    },
}
</script>

<template>
    <Layout>
        <div class="row page-title align-items-center">
            <div class="col-sm-4"><h4 class="mb-1 mt-0">{{ title }}</h4></div>
        </div>

        <div class="card">
            <div class="card-body">
                <div class="row align-items-end">
                    <div class="col-md-3">
                        <label>Период</label>
                        <flat-pickr v-model="selectedDate" :config="dateConfig" class="form-control" @on-change="onChangePeriod" />
                    </div>
                    <div class="col-md-2">
                        <label>Тип ботов</label>
                        <select v-model="fl_ai" class="form-control" @change="getData(); loadBotOptions()">
                            <option value="">Все</option>
                            <option value="1">Только AI</option>
                            <option value="0">Только не-AI</option>
                        </select>
                    </div>
                    <div class="col-md-2">
                        <label>Группировка</label>
                        <select v-model="group_by" class="form-control" @change="getData">
                            <option value="day">По дням</option>
                            <option value="bot">По ботам</option>
                            <option value="user">По клиентам</option>
                            <option value="calls">По звонкам</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label>Боты</label>
                        <b-dropdown
                            :text="botDropdownText"
                            variant="outline-secondary"
                            class="w-100 economy-bot-dd"
                            menu-class="economy-bot-menu"
                            block
                            no-flip
                        >
                            <b-dropdown-form @submit.prevent>
                                <input v-model="botSearch" type="text" class="form-control form-control-sm mb-2" placeholder="Поиск бота…" />
                                <div class="economy-bot-list">
                                    <b-form-checkbox
                                        v-for="b in filteredBotOptions"
                                        :key="b.id"
                                        v-model="bot_ids"
                                        :value="b.id"
                                        class="mb-1"
                                    >{{ b.name }}</b-form-checkbox>
                                    <div v-if="!filteredBotOptions.length" class="text-muted small">ничего не найдено</div>
                                </div>
                                <div class="mt-2 d-flex justify-content-between">
                                    <button class="btn btn-sm btn-light" @click="bot_ids = []; getData()">Очистить</button>
                                    <button class="btn btn-sm btn-primary" @click="getData()">Применить</button>
                                </div>
                            </b-dropdown-form>
                        </b-dropdown>
                    </div>
                    <div class="col-md-2">
                        <button class="btn btn-light mr-1" @click="resetFilters">Сбросить</button>
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col-md-12 text-right">
                        <a :href="exportUrl('days')" class="btn btn-outline-primary btn-sm mr-1">XLSX дни</a>
                        <a :href="exportUrl('bots')" class="btn btn-outline-primary btn-sm mr-1">XLSX боты</a>
                        <a :href="exportUrl('calls')" class="btn btn-outline-primary btn-sm">XLSX звонки</a>
                    </div>
                </div>
                <div v-if="bot_id || user_id || bot_ids.length" class="mt-2">
                    <span v-if="bot_id" class="badge badge-info mr-1">бот #{{ bot_id }}</span>
                    <span v-for="bid in bot_ids" :key="'sel' + bid" class="badge badge-primary mr-1">{{ (botOptions.find((b) => b.id === bid) || {}).name || '#' + bid }}</span>
                    <span v-if="user_id" class="badge badge-info">клиент {{ user_label || ('#' + user_id) }}</span>
                </div>
            </div>
        </div>

        <div v-if="totals" class="row">
            <div class="col-md-2"><div class="card"><div class="card-body text-center"><h6>Доход</h6><h4>{{ fmt(totals.revenue) }} ₽</h4></div></div></div>
            <div class="col-md-2"><div class="card"><div class="card-body text-center"><h6>Себестоимость</h6><h4>{{ fmt(totals.buy_cost) }} ₽</h4></div></div></div>
            <div class="col-md-2"><div class="card"><div class="card-body text-center"><h6>Маржа</h6><h4>{{ fmt(totals.margin) }} ₽</h4></div></div></div>
            <div class="col-md-2"><div class="card"><div class="card-body text-center"><h6>Маржа %</h6><h4>{{ totals.margin_pct !== null ? totals.margin_pct + '%' : '—' }}</h4></div></div></div>
            <div class="col-md-2"><div class="card"><div class="card-body text-center"><h6>Звонков</h6><h4>{{ fmt(totals.calls_cnt) }}</h4></div></div></div>
            <div class="col-md-2"><div class="card"><div class="card-body text-center"><h6>Минут</h6><h4>{{ fmt(totals.minutes) }}</h4></div></div></div>
        </div>

        <div v-if="totals" class="card">
            <div class="card-body py-2">
                <small>
                    Разложение себестоимости: этикетка <b>{{ fmt(totals.buy_attempts) }} ₽</b> ({{ fmt(totals.attempts_cnt) }} попыток × 0.60) ·
                    телефония <b>{{ fmt(totals.buy_telephony) }} ₽</b> ({{ fmt(totals.minutes_out) }} мин. исходящих × 1.35)
                    <span v-for="k in providerKeys" :key="k"> · {{ providerLabels[k] || k }} <b>{{ fmt((totals.providers && totals.providers[k]) || 0) }} ₽</b></span> ·
                    прочее <b>{{ fmt(totals.buy_other) }} ₽</b> ·
                    комиссия партнёров <b>{{ fmt(totals.partner_commission) }} ₽</b>
                    <br /><span class="text-muted">объёмы: {{ fmt(totals.qty_llm_tokens_in) }} ток. вход (кэш {{ fmt(totals.qty_llm_tokens_cached) }}) · {{ fmt(totals.qty_tts_chars) }} симв. озвучки · {{ fmt(totals.qty_stt_sec) }} сек. распознавания</span>
                </small>
            </div>
        </div>

        <div v-if="group_by !== 'calls'" class="card">
            <div class="card-body">
                <b-table
                    :items="rows"
                    :fields="tableFields"
                    :busy="loading"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    hover
                    responsive
                    small
                    @row-clicked="rowClicked"
                >
                    <template v-slot:thead-top>
                        <b-tr class="table-active font-weight-bold">
                            <b-td>ИТОГО</b-td>
                            <b-td v-for="f in tableFields.slice(1)" :key="'tot' + f.key">
                                <template v-if="f.key === 'margin_pct'">{{ colTotals.margin_pct !== null ? colTotals.margin_pct + '%' : '—' }}</template>
                                <template v-else-if="isCostKey(f.key)">{{ fmt(colTotals[f.key]) }}{{ pctStr(colTotals[f.key], colTotals.revenue) }}</template>
                                <template v-else>{{ fmt(colTotals[f.key]) }}</template>
                            </b-td>
                        </b-tr>
                    </template>
                    <template v-slot:cell(grp)="data">
                        <template v-if="group_by === 'user' && userLink(data.item.grp)">
                            <router-link :to="userLink(data.item.grp)" @click.native.stop>{{ userTitle(data.item.grp) }}</router-link><span class="text-muted">{{ userBalanceStr(data.item.grp) }}</span>
                        </template>
                        <template v-else>{{ rowLabel(data.item) }}</template>
                    </template>
                    <template v-slot:cell(margin_pct)="data">{{ data.item.margin_pct !== null ? data.item.margin_pct + '%' : '—' }}</template>
                </b-table>
                <small v-if="group_by === 'bot'" class="text-muted">Клик по боту — фильтр + список его звонков. Клик по заголовку столбца — сортировка.</small>
                <small v-if="group_by === 'user'" class="text-muted">Клик по имени — карточка клиента; клик по строке — фильтр по клиенту. Клик по заголовку столбца — сортировка.</small>
            </div>
        </div>

        <div v-if="showCalls" class="card">
            <div class="card-body">
                <h5>Звонки ({{ calls.total }})</h5>
                <table class="table table-sm table-hover">
                    <thead><tr><th>Дата</th><th>Бот</th><th>Клиент</th><th>Статус</th><th>Сек</th><th>Попыт.</th><th>Доход ₽</th><th>Этикетка</th><th>Телеф.</th><th v-for="k in callProviderKeys" :key="k">{{ callProviderLabels[k] || k }}</th><th>Прочее</th><th>Себест. ₽</th><th>Маржа ₽</th><th></th></tr></thead>
                    <tbody>
                        <tr v-for="c in calls.rows" :key="c.uuid">
                            <td>{{ c.created_at }}</td>
                            <td>{{ c.bot_name }} <span v-if="c.fl_ai">🤖</span></td>
                            <td>
                                <router-link v-if="c.user_uuid" :to="'/account/users/' + c.user_uuid + '/edit'">{{ c.user_name || c.user_phone || ('#' + c.user_id) }}</router-link>
                                <template v-else>{{ c.user_name || c.user_phone || ('#' + c.user_id) }}</template>
                            </td>
                            <td>{{ c.status }}</td>
                            <td>{{ c.bill_sec }}</td>
                            <td>{{ c.attempt || 1 }}</td>
                            <td>{{ c.cost }}</td>
                            <td>{{ c.buy_attempts }}{{ pctStr(c.buy_attempts, c.cost) }}</td>
                            <td>{{ c.buy_telephony }}{{ pctStr(c.buy_telephony, c.cost) }}</td>
                            <td v-for="k in callProviderKeys" :key="k">{{ (c.providers && c.providers[k]) || 0 }}{{ pctStr((c.providers && c.providers[k]) || 0, c.cost) }}</td>
                            <td>{{ c.buy_other }}{{ pctStr(c.buy_other, c.cost) }}</td>
                            <td>{{ c.buy_cost }}{{ pctStr(c.buy_cost, c.cost) }}</td>
                            <td>{{ c.margin }}</td>
                            <td><button class="btn btn-xs btn-outline-secondary" @click="openCallDetail(c.uuid)">раскладка</button></td>
                        </tr>
                    </tbody>
                </table>
                <button v-if="calls.rows.length < calls.total" class="btn btn-sm btn-light" @click="calls.page++; getCalls()">Ещё</button>
            </div>
        </div>

        <b-modal id="call-detail-modal" size="lg" title="Раскладка звонка" hide-footer>
            <div v-if="callDetail">
                <p class="mb-1">
                    <b>{{ callDetail.created_at }}</b> · {{ callDetail.status }} · {{ callDetail.bill_sec }} сек ·
                    доход <b>{{ callDetail.cost }} ₽</b> · себестоимость <b>{{ callDetail.buy_cost }} ₽</b> ·
                    маржа <b>{{ callDetail.margin }} ₽</b>
                </p>
                <div v-for="(attempt, ai) in callDetail.billing" :key="ai">
                    <table class="table table-sm">
                        <thead><tr><th>Операция</th><th>Объём</th><th>Клиенту ₽</th><th>Закупка ₽</th></tr></thead>
                        <tbody>
                            <tr v-for="(item, ii) in attempt" :key="ii">
                                <td>{{ billingLabel(item) }}</td>
                                <td>{{ billingQty(item) }}</td>
                                <td>{{ item.cost !== undefined ? item.cost : '—' }}</td>
                                <td>{{ item.buy !== undefined ? item.buy : '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </b-modal>
    </Layout>
</template>

<style>
.economy-bot-menu {
    min-width: 320px;
    padding: 10px;
}
.economy-bot-list {
    max-height: 260px;
    overflow-y: auto;
}
.economy-bot-dd > .btn {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
